// ts-rest contract for the client portal — "Septima Link" (PRD #71).
//
// The portal is the client-facing surface of the platform. It is gated by a
// persistent per-client credential (slug + password): the client signs in at
// `/portal/<slug>` and a signed `septima-portal-session` cookie is set. The
// password is stored hashed only — staff can regenerate it (the new plaintext
// is returned exactly once by the mutation) but never read it back.
//
// Object Types this contract owns:
//   ClientPortalSession      — login + activity record per sign-in.
//   ClientPortalMessage      — a secure two-way thread message with the IM.
//   ClientDocumentAccess     — a per-document download log entry.
//   ClientPortalNotification — an outstanding prompt for a client.
//
// Endpoints under /api/portal/*:
//   POST /api/portal/login                — client signs in (slug + password)
//   GET  /api/portal/credential/:clientId — staff read: slug only, no password
//   POST /api/portal/credential           — staff mint (password returned once)
//   POST /api/portal/credential/regenerate — staff rotate (password returned once)
//   GET  /api/portal/session              — current portal session (from cookie)
//   POST /api/portal/logout               — end the portal session
//   GET  /api/portal/dashboard            — dashboard payload for the session
//   GET  /api/portal/portfolio            — holdings + allocation + performance
//   GET  /api/portal/documents            — document library for the client
//   POST /api/portal/documents/:id/download — log a document download
//   GET  /api/portal/messages             — message thread for the client
//   POST /api/portal/messages             — client posts a message
//   GET  /api/portal/profile              — contact + preferences + security
//   PATCH /api/portal/profile             — update comms preferences
//   GET  /api/portal/notifications        — outstanding prompts

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

/** Lifecycle of a portal session. */
export const portalSessionStatusSchema = z.enum(['active', 'ended', 'expired']);
export type PortalSessionStatus = z.infer<typeof portalSessionStatusSchema>;

/** Who authored a portal message. */
export const portalMessageRoleSchema = z.enum(['im', 'client', 'ops']);
export type PortalMessageRole = z.infer<typeof portalMessageRoleSchema>;

/** How a portal message was delivered — mirrors ClientCommunication. */
export const portalDeliveryChannelSchema = z.enum(['portal', 'outlook', 'manual']);
export type PortalDeliveryChannel = z.infer<typeof portalDeliveryChannelSchema>;

/** Document category for the portal library. */
export const portalDocumentCategorySchema = z.enum([
    'statement',
    'tax',
    'commentary',
    'narrative',
    'contract',
    'suitability',
    'onboarding',
]);
export type PortalDocumentCategory = z.infer<typeof portalDocumentCategorySchema>;

/** Kind of outstanding client prompt. */
export const portalNotificationKindSchema = z.enum([
    'fact_find_refresh',
    'document_review',
    'new_message',
    'welcome',
]);
export type PortalNotificationKind = z.infer<typeof portalNotificationKindSchema>;

export const portalNotificationStatusSchema = z.enum(['outstanding', 'cleared']);
export type PortalNotificationStatus = z.infer<typeof portalNotificationStatusSchema>;

// ---------------------------------------------------------------------------
// Object Types
// ---------------------------------------------------------------------------

/** A document citation footnote — mirrors the agent glass-box trace shape. */
export const portalCitationSchema = z.object({
    marker: z.string(),
    source: z.string(),
    detail: z.string(),
});
export type PortalCitation = z.infer<typeof portalCitationSchema>;

/** ClientPortalSession — a login + activity record. */
export const clientPortalSessionSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    status: portalSessionStatusSchema,
    /** Stub client IP — Consumer Duty audit shape. */
    ipAddress: z.string(),
    userAgent: z.string(),
    issuedBy: z.string(),
    issuedAt: z.string(),
    /** Set once the token is consumed. */
    loginAt: z.string().nullable(),
    /** Token expiry — a token is one-shot and time-limited. */
    expiresAt: z.string(),
    endedAt: z.string().nullable(),
    /** Append-only activity trail — login, downloads, messages, logout. */
    activity: z.array(
        z.object({
            at: z.string(),
            action: z.string(),
            detail: z.string(),
        }),
    ),
});
export type ClientPortalSession = z.infer<typeof clientPortalSessionSchema>;

/** ClientPortalMessage — a secure thread message. */
export const clientPortalMessageSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    threadId: z.string(),
    role: portalMessageRoleSchema,
    authorName: z.string(),
    body: z.string(),
    /** Channel the message arrived through — 'portal' for native, 'outlook' for
     *  an EMISSARY draft sent via the IM's Outlook mailbox. */
    deliveryChannel: portalDeliveryChannelSchema,
    /** Set when the message originated from a composer draft. */
    sourceCommunicationId: z.string().nullable(),
    createdAt: z.string(),
    readByClient: z.boolean(),
});
export type ClientPortalMessage = z.infer<typeof clientPortalMessageSchema>;

/** A document in the portal library. */
export const portalLibraryDocumentSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    title: z.string(),
    category: portalDocumentCategorySchema,
    publishedOn: z.string(),
    sizeKb: z.number().int(),
    /** Inline body for narrative-type documents (ATLAS / HERALD). */
    body: z.string().nullable(),
    /** Citation footnotes for narrative documents. */
    citations: z.array(portalCitationSchema),
    /** Set when the document originated from a composer artefact. */
    sourceArtefactId: z.string().nullable(),
});
export type PortalLibraryDocument = z.infer<typeof portalLibraryDocumentSchema>;

/** ClientDocumentAccess — a per-document download log entry. */
export const clientDocumentAccessSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    documentId: z.string(),
    documentTitle: z.string(),
    sessionId: z.string(),
    accessedAt: z.string(),
    ipAddress: z.string(),
});
export type ClientDocumentAccess = z.infer<typeof clientDocumentAccessSchema>;

/** ClientPortalNotification — an outstanding prompt. */
export const clientPortalNotificationSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    kind: portalNotificationKindSchema,
    status: portalNotificationStatusSchema,
    title: z.string(),
    detail: z.string(),
    /** Portal route the prompt links to. */
    href: z.string(),
    createdAt: z.string(),
    clearedAt: z.string().nullable(),
});
export type ClientPortalNotification = z.infer<typeof clientPortalNotificationSchema>;

// ---------------------------------------------------------------------------
// Aggregate payloads
// ---------------------------------------------------------------------------

export const portalHoldingSchema = z.object({
    id: z.string(),
    name: z.string(),
    ticker: z.string(),
    assetClass: z.string(),
    isin: z.string().nullable(),
    quantity: z.number(),
    /** Current value (the ValuationGrid figure of record). */
    valueGbp: z.number(),
    /** Acquisition cost (ValuationGrid book cost). */
    bookCostGbp: z.number(),
    /** valueGbp − bookCostGbp (unrealised gain/loss since purchase). */
    gainLossGbp: z.number(),
    weight: z.number(),
    /** Live year-to-date market move (%), from Yahoo; 0 where unpriced. */
    gainLossYtd: z.number(),
    /** Live Yahoo price per unit in GBP; null where the ISIN can't be priced. */
    livePriceGbp: z.number().nullable(),
});
export type PortalHolding = z.infer<typeof portalHoldingSchema>;

/** A client cash account on the overdrawn-cash report — one negative balance. */
export const portalOverdrawnAccountSchema = z.object({
    /** Cash account reference (e.g. "2620 - D 070122-01"). */
    accountReference: z.string(),
    /** Human cash-book name (e.g. "Acme Holdings - Dealing"). */
    accountName: z.string(),
    currency: z.string(),
    /** Posted balance — negative for an overdrawn account. */
    balanceGbp: z.number(),
});
export type PortalOverdrawnAccount = z.infer<typeof portalOverdrawnAccountSchema>;

/** A single actionable item on the Action Centre. */
export const portalActionItemSchema = z.object({
    id: z.string(),
    kind: z.enum(['overdrawn', 'document', 'message', 'notification']),
    /** Severity drives the badge tone — `critical` (overdrawn) vs `attention`. */
    severity: z.enum(['critical', 'attention', 'info']),
    title: z.string(),
    detail: z.string(),
    href: z.string(),
});
export type PortalActionItem = z.infer<typeof portalActionItemSchema>;

/** A compact document reference for the dashboard "latest documents" panel. */
export const portalDocumentRefSchema = z.object({
    id: z.string(),
    title: z.string(),
    category: portalDocumentCategorySchema,
    publishedOn: z.string(),
});
export type PortalDocumentRef = z.infer<typeof portalDocumentRefSchema>;

export const portalDashboardSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    imName: z.string(),
    ytdPerformancePct: z.number(),
    /** Benchmark YTD (ARC band) for the dashboard benchmark line. */
    benchmarkYtdPct: z.number(),
    /** Net cash across all the client's cash lines (can be negative). */
    netCashGbp: z.number(),
    /** Official figure-of-record valuation (ValuationGrid sum). */
    officialValuationGbp: z.number(),
    /** ISO date the official ValuationGrid figures were loaded (empty if none). */
    officialValuationDate: z.string(),
    /** Live estimate from Yahoo prices layered over the grid; equals official
     *  when no line can be priced. */
    estimatedLiveValueGbp: z.number(),
    /** Custodian(s) holding the client's accounts — comma-joined, '' if unknown. */
    custodian: z.string(),
    /** Base reporting currency (always GBP for the figures of record). */
    baseCurrency: z.string(),
    /** Risk band / categorisation label, '' when none resolves. */
    riskBand: z.string(),
    mandate: z.string(),
    benchmark: z.string(),
    /** Client cash accounts that are overdrawn (negative), from the OD report. */
    overdrawnAccounts: z.array(portalOverdrawnAccountSchema),
    /** Newest few documents for the dashboard mini-section. */
    latestDocuments: z.array(portalDocumentRefSchema),
    /** Real actionable items (overdrawn, documents awaiting, messages). */
    actionItems: z.array(portalActionItemSchema),
    notifications: z.array(clientPortalNotificationSchema),
    unreadMessageCount: z.number().int(),
});
export type PortalDashboard = z.infer<typeof portalDashboardSchema>;

export const portalPortfolioSchema = z.object({
    clientId: z.string(),
    valuationDate: z.string(),
    totalValueGbp: z.number(),
    ytdPerformancePct: z.number(),
    benchmarkYtdPct: z.number(),
    feesPaidThisPeriodGbp: z.number(),
    holdings: z.array(portalHoldingSchema),
    performance: z.array(
        z.object({ period: z.string(), portfolioPct: z.number(), benchmarkPct: z.number() }),
    ),
});
export type PortalPortfolio = z.infer<typeof portalPortfolioSchema>;

// ---------------------------------------------------------------------------
// Cash
// ---------------------------------------------------------------------------

/** One cash account on the portal cash surface. */
export const portalCashAccountSchema = z.object({
    id: z.string(),
    accountReference: z.string(),
    accountName: z.string(),
    currency: z.string(),
    /** Posted balance in the account's reporting currency (GBP figures of record). */
    balanceGbp: z.number(),
    /** True when the posted balance is negative. */
    overdrawn: z.boolean(),
    /** Source of the line — the figure-of-record ValuationGrid cash line, or the
     *  overdrawn-cash operational report. */
    source: z.enum(['valuation_grid', 'overdrawn_report']),
});
export type PortalCashAccount = z.infer<typeof portalCashAccountSchema>;

/** Per-currency cash rollup. */
export const portalCashByCurrencySchema = z.object({
    currency: z.string(),
    totalGbp: z.number(),
});
export type PortalCashByCurrency = z.infer<typeof portalCashByCurrencySchema>;

export const portalCashSchema = z.object({
    clientId: z.string(),
    /** Net cash across every account (can be negative). */
    netCashGbp: z.number(),
    /** Sum of positive cash balances only. */
    availableCashGbp: z.number(),
    /** Number of overdrawn accounts. */
    overdrawnCount: z.number().int(),
    /** Total of the overdrawn (negative) balances. */
    overdrawnTotalGbp: z.number(),
    accounts: z.array(portalCashAccountSchema),
    byCurrency: z.array(portalCashByCurrencySchema),
});
export type PortalCash = z.infer<typeof portalCashSchema>;

// ---------------------------------------------------------------------------
// Exposure
// ---------------------------------------------------------------------------

/** One band of an exposure breakdown (asset class / currency). */
export const portalExposureBandSchema = z.object({
    label: z.string(),
    valueGbp: z.number(),
    weight: z.number(),
});
export type PortalExposureBand = z.infer<typeof portalExposureBandSchema>;

/** A top-N concentration line. */
export const portalConcentrationLineSchema = z.object({
    name: z.string(),
    isin: z.string().nullable(),
    valueGbp: z.number(),
    weight: z.number(),
});
export type PortalConcentrationLine = z.infer<typeof portalConcentrationLineSchema>;

export const portalExposureSchema = z.object({
    clientId: z.string(),
    totalValueGbp: z.number(),
    byAssetClass: z.array(portalExposureBandSchema),
    byCurrency: z.array(portalExposureBandSchema),
    /** Top-10 holdings by value + the share they represent of the portfolio. */
    topHoldings: z.array(portalConcentrationLineSchema),
    /** Combined weight of the top-10 holdings (%). */
    topTenConcentrationPct: z.number(),
});
export type PortalExposure = z.infer<typeof portalExposureSchema>;

// ---------------------------------------------------------------------------
// Holding detail
// ---------------------------------------------------------------------------

export const portalHoldingDetailSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    name: z.string(),
    ticker: z.string(),
    isin: z.string().nullable(),
    assetClass: z.string(),
    currency: z.string(),
    quantity: z.number(),
    valueGbp: z.number(),
    /** Acquisition cost — 0 when the grid carries none (rendered as '—'). */
    bookCostGbp: z.number(),
    gainLossGbp: z.number(),
    weight: z.number(),
    gainLossYtd: z.number(),
    livePriceGbp: z.number().nullable(),
    /** Portfolio (account) reference this line sits within. */
    portfolioRef: z.string(),
});
export type PortalHoldingDetail = z.infer<typeof portalHoldingDetailSchema>;

export const portalProfileSchema = z.object({
    clientId: z.string(),
    clientName: z.string(),
    dateOfBirth: z.string(),
    email: z.string(),
    phone: z.string(),
    address: z.string(),
    nationality: z.string(),
    taxResidency: z.string(),
    imName: z.string(),
    assistantName: z.string(),
    clientSince: z.string(),
    categorisation: z.string(),
    /** Editable communication preferences. */
    preferredChannel: z.enum(['email', 'phone', 'portal']),
    marketingOptIn: z.boolean(),
    /** MFA / biometric settings — stubbed. */
    mfaMethod: z.enum(['authenticator', 'sms', 'none']),
    biometricEnabled: z.boolean(),
    sessionTimeoutMinutes: z.number().int(),
    lastLoginAt: z.string().nullable(),
});
export type PortalProfile = z.infer<typeof portalProfileSchema>;

// ---------------------------------------------------------------------------
// Request / response schemas
// ---------------------------------------------------------------------------

// Portal login uses a persistent per-client credential (slug + password), not
// a one-shot magic link. `slug` is the short, non-UUID id in the
// `/portal/<slug>` URL; `password` is delivered to the client by their IM.
export const loginRequestSchema = z.object({
    slug: z.string(),
    password: z.string(),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
    ok: z.boolean(),
    session: clientPortalSessionSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

// Staff-facing credential READ payload (Desk client page / onboarding
// portal-access stage). Only the slug — the `/portal/<slug>` address — is ever
// readable: the password is stored as a bcrypt hash and cannot be recovered,
// so this shape deliberately carries no password field. Staff who need to
// furnish a password regenerate it (`regenerateCredential`), which returns the
// new plaintext exactly once. `clientId` is the canonical (person) Client id.
export const portalCredentialSchema = z.object({
    hasCredential: z.boolean(),
    clientId: z.string(),
    slug: z.string().nullable(),
});
export type PortalCredential = z.infer<typeof portalCredentialSchema>;

// Mutation response for `ensureCredential`. `password` is the freshly minted
// plaintext when THIS call created the credential, and null when one already
// existed (its password is hashed and unrecoverable — regenerate to issue a
// new one). The plaintext exists only in this response; it is never persisted
// or readable again.
export const portalCredentialIssueSchema = z.object({
    clientId: z.string(),
    slug: z.string(),
    password: z.string().nullable(),
});
export type PortalCredentialIssue = z.infer<typeof portalCredentialIssueSchema>;

// Mutation response for `regenerateCredential` — the one and only time the new
// password is visible. The previous password stops working immediately; the
// slug (and therefore the portal URL) is unchanged.
export const portalCredentialRegenerateSchema = z.object({
    clientId: z.string(),
    slug: z.string(),
    password: z.string(),
});
export type PortalCredentialRegenerate = z.infer<typeof portalCredentialRegenerateSchema>;

export const ensureCredentialRequestSchema = z.object({
    clientId: z.string(),
});
export type EnsureCredentialRequest = z.infer<typeof ensureCredentialRequestSchema>;

export const regenerateCredentialRequestSchema = z.object({
    clientId: z.string(),
});
export type RegenerateCredentialRequest = z.infer<typeof regenerateCredentialRequestSchema>;

export const portalSessionResponseSchema = z.object({
    ok: z.boolean(),
    session: clientPortalSessionSchema.nullable(),
});
export type PortalSessionResponse = z.infer<typeof portalSessionResponseSchema>;

export const postMessageRequestSchema = z.object({
    body: z.string().min(1),
});
export type PostMessageRequest = z.infer<typeof postMessageRequestSchema>;

export const portalMessagesResponseSchema = z.object({
    messages: z.array(clientPortalMessageSchema),
});
export type PortalMessagesResponse = z.infer<typeof portalMessagesResponseSchema>;

export const portalDocumentsResponseSchema = z.object({
    documents: z.array(portalLibraryDocumentSchema),
});
export type PortalDocumentsResponse = z.infer<typeof portalDocumentsResponseSchema>;

export const documentDownloadResponseSchema = z.object({
    ok: z.boolean(),
    access: clientDocumentAccessSchema,
});
export type DocumentDownloadResponse = z.infer<typeof documentDownloadResponseSchema>;

export const portalNotificationsResponseSchema = z.object({
    notifications: z.array(clientPortalNotificationSchema),
});
export type PortalNotificationsResponse = z.infer<typeof portalNotificationsResponseSchema>;

export const updateProfileRequestSchema = z.object({
    preferredChannel: z.enum(['email', 'phone', 'portal']).optional(),
    marketingOptIn: z.boolean().optional(),
    mfaMethod: z.enum(['authenticator', 'sms', 'none']).optional(),
});
export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;

export const portalHoldingDetailResponseSchema = z.object({
    holding: portalHoldingDetailSchema,
});
export type PortalHoldingDetailResponse = z.infer<typeof portalHoldingDetailResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const portalContract = c.router(
    {
        login: {
            method: 'POST',
            path: '/portal/login',
            body: loginRequestSchema,
            responses: { 200: loginResponseSchema, 401: errorBody },
            summary: 'Client signs in with their portal slug + password',
        },
        // Staff (Desk) — read the client's portal address. Never the password:
        // only the hash is stored, so there is nothing to read back.
        getCredential: {
            method: 'GET',
            path: '/portal/credential/:clientId',
            responses: { 200: portalCredentialSchema, 404: errorBody },
            summary: 'Portal slug for a client (staff; password is never readable)',
        },
        // Staff (Desk) — mint a credential on demand for a client that has none.
        // The response carries the new password exactly once (null when the
        // credential already existed).
        ensureCredential: {
            method: 'POST',
            path: '/portal/credential',
            body: ensureCredentialRequestSchema,
            responses: { 200: portalCredentialIssueSchema, 404: errorBody },
            summary: 'Generate portal access for a client (staff)',
        },
        // Staff (Desk) — rotate the client's portal password. The response is
        // the only place the new plaintext ever appears; the old password stops
        // working immediately and the slug is unchanged.
        regenerateCredential: {
            method: 'POST',
            path: '/portal/credential/regenerate',
            body: regenerateCredentialRequestSchema,
            responses: { 200: portalCredentialRegenerateSchema, 404: errorBody },
            summary: 'Regenerate the portal password for a client (staff)',
        },
        getSession: {
            method: 'GET',
            path: '/portal/session',
            responses: { 200: portalSessionResponseSchema },
            summary: 'Current portal session from the cookie',
        },
        logout: {
            method: 'POST',
            path: '/portal/logout',
            body: z.object({}),
            responses: { 200: z.object({ ok: z.boolean() }) },
            summary: 'End the current portal session',
        },
        getDashboard: {
            method: 'GET',
            path: '/portal/dashboard',
            responses: { 200: portalDashboardSchema, 401: errorBody },
            summary: 'Dashboard payload for the signed-in client',
        },
        getPortfolio: {
            method: 'GET',
            path: '/portal/portfolio',
            responses: { 200: portalPortfolioSchema, 401: errorBody },
            summary: 'Portfolio holdings, allocation and performance',
        },
        getHolding: {
            method: 'GET',
            path: '/portal/holdings/:isin',
            responses: {
                200: portalHoldingDetailResponseSchema,
                401: errorBody,
                404: errorBody,
            },
            summary: 'Detail for a single holding by ISIN (SubjectScoped)',
        },
        getCash: {
            method: 'GET',
            path: '/portal/cash',
            responses: { 200: portalCashSchema, 401: errorBody },
            summary: 'Cash accounts, currency breakdown and overdrawn alerts',
        },
        getExposure: {
            method: 'GET',
            path: '/portal/exposure',
            responses: { 200: portalExposureSchema, 401: errorBody },
            summary: 'Asset-class / currency exposure and top-10 concentration',
        },
        listDocuments: {
            method: 'GET',
            path: '/portal/documents',
            responses: { 200: portalDocumentsResponseSchema, 401: errorBody },
            summary: 'Document library for the signed-in client',
        },
        downloadDocument: {
            method: 'POST',
            path: '/portal/documents/:documentId/download',
            body: z.object({}),
            responses: {
                200: documentDownloadResponseSchema,
                401: errorBody,
                404: errorBody,
            },
            summary: 'Log a document download — requires a valid portal session',
        },
        listMessages: {
            method: 'GET',
            path: '/portal/messages',
            responses: { 200: portalMessagesResponseSchema, 401: errorBody },
            summary: 'Message thread for the signed-in client',
        },
        postMessage: {
            method: 'POST',
            path: '/portal/messages',
            body: postMessageRequestSchema,
            responses: {
                200: portalMessagesResponseSchema,
                400: errorBody,
                401: errorBody,
            },
            summary: 'Client posts a message into the thread',
        },
        getProfile: {
            method: 'GET',
            path: '/portal/profile',
            responses: { 200: portalProfileSchema, 401: errorBody },
            summary: 'Contact details, preferences and security settings',
        },
        updateProfile: {
            method: 'PATCH',
            path: '/portal/profile',
            body: updateProfileRequestSchema,
            responses: { 200: portalProfileSchema, 401: errorBody },
            summary: 'Update communication preferences and MFA settings',
        },
        listNotifications: {
            method: 'GET',
            path: '/portal/notifications',
            responses: { 200: portalNotificationsResponseSchema, 401: errorBody },
            summary: 'Outstanding prompts for the signed-in client',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
